import axios from 'axios'
import { basePath } from '../api'
import {
  AiBrief,
  DimensionMetrics,
  LeaderMetrics,
  MetricsOverview,
  PagedCompletedReviews,
  PagedSkippedReviews,
  PagedUserMetrics,
  ReviewMetrics,
} from '../types/metrics'

const BASE = `${basePath}/metrics/dashboard`

/** Default timeout for fast endpoints (overview, reviews, users, dimensions, leaders). */
const DEFAULT_TIMEOUT_MS = 15_000

/** Extended timeout for AI brief — OpenAI generation can take 10–20s on first call. */
const AI_BRIEF_TIMEOUT_MS = 60_000

/** Extended timeout for CSV export — full dataset at scale. */
const EXPORT_TIMEOUT_MS = 30_000

/**
 * Platform-wide snapshot: total users, login stats, review counts.
 */
export const getMetricsOverview = (): Promise<MetricsOverview> =>
  axios.get<MetricsOverview>(`${BASE}/overview`, { timeout: DEFAULT_TIMEOUT_MS }).then(r => r.data)

/**
 * Review-activity metrics for a time window.
 * Pass ISO datetime strings (e.g. "2024-01-01T00:00:00").
 */
export const getReviewMetrics = (after: string, before: string): Promise<ReviewMetrics> =>
  axios
    .get<ReviewMetrics>(`${BASE}/reviews`, { params: { after, before }, timeout: DEFAULT_TIMEOUT_MS })
    .then(r => r.data)

/**
 * Paginated per-reviewer breakdown.
 * Replaces the manual spreadsheet admins previously maintained.
 * When `search` is provided, the backend filters by case-insensitive
 * substring match on name OR email, and totalElements reflects the
 * filtered count.
 */
export const getUserMetrics = (page: number, size: number, search?: string): Promise<PagedUserMetrics> => {
  const params: Record<string, unknown> = { page, size }
  const trimmed = search?.trim()
  if (trimmed) params.search = trimmed
  return axios.get<PagedUserMetrics>(`${BASE}/users`, { params, timeout: DEFAULT_TIMEOUT_MS }).then(r => r.data)
}

/**
 * Cohort-wide dimension averages with percentile bands and per-category breakdown.
 * Not time-range filtered — covers all completed reviews in the active period.
 */
export const getDimensionMetrics = (): Promise<DimensionMetrics> =>
  axios.get<DimensionMetrics>(`${BASE}/dimensions`, { timeout: DEFAULT_TIMEOUT_MS }).then(r => r.data)

/**
 * Leader-level metrics: report count, average score, talent-rank breakdown.
 */
export const getLeaderMetrics = (): Promise<LeaderMetrics[]> =>
  axios.get<LeaderMetrics[]>(`${BASE}/leaders`, { timeout: DEFAULT_TIMEOUT_MS }).then(r => r.data)

/**
 * AI-generated (or template fallback) executive summary for the selected time window.
 * Uses an extended timeout because OpenAI generation can take 10–20s on the first
 * call for a given time range (subsequent calls are cached by Caffeine on the backend).
 */
export const getAiBrief = (after: string, before: string): Promise<AiBrief> =>
  axios.get<AiBrief>(`${BASE}/ai-brief`, { params: { after, before }, timeout: AI_BRIEF_TIMEOUT_MS }).then(r => r.data)

/**
 * Common shape for review-list query options. Each list/export endpoint accepts
 * the same filter set, so we centralise the param-hygiene logic below.
 */
type ReviewQueryOpts<TSort extends string> = {
  periodId?: number
  reviewerId?: number
  search?: string
  reviewerName?: string
  sort?: TSort
}

/**
 * Drops empty/undefined filters and trims search strings, mirroring the
 * `getUserMetrics` pattern so the backend can apply its own defaults.
 */
const buildReviewQueryParams = <TSort extends string>(
  opts: ReviewQueryOpts<TSort> | undefined,
  base: Record<string, unknown> = {}
): Record<string, unknown> => {
  const params: Record<string, unknown> = { ...base }
  if (opts?.periodId !== undefined) params.periodId = opts.periodId
  if (opts?.reviewerId !== undefined) params.reviewerId = opts.reviewerId
  const trimmedSearch = opts?.search?.trim()
  if (trimmedSearch) params.search = trimmedSearch
  const trimmedReviewerName = opts?.reviewerName?.trim()
  if (trimmedReviewerName) params.reviewerName = trimmedReviewerName
  if (opts?.sort) params.sort = opts.sort
  return params
}

/**
 * Paginated list of completed reviews filtered by period, reviewer, and/or search.
 */
export type CompletedReviewsSortField =
  | 'finishDate'
  | 'periodName'
  | 'talentRank'
  | 'averageScore'
  | 'reviewerName'
  | 'employeeName'

export type CompletedReviewsSort = `${CompletedReviewsSortField},${'asc' | 'desc'}`

export const getCompletedReviews = (
  page: number,
  size: number,
  opts?: ReviewQueryOpts<CompletedReviewsSort>
): Promise<PagedCompletedReviews> =>
  axios
    .get<PagedCompletedReviews>(`${BASE}/completed-reviews`, {
      params: buildReviewQueryParams(opts, { page, size }),
      timeout: DEFAULT_TIMEOUT_MS,
    })
    .then(r => r.data)

/**
 * Paginated list of skipped reviews filtered by period, reviewer, and/or search.
 */
export type SkippedReviewsSortField = 'skipDate' | 'periodName' | 'reviewerName' | 'employeeName' | 'reason'

export type SkippedReviewsSort = `${SkippedReviewsSortField},${'asc' | 'desc'}`

export const getSkippedReviews = (
  page: number,
  size: number,
  opts?: ReviewQueryOpts<SkippedReviewsSort>
): Promise<PagedSkippedReviews> =>
  axios
    .get<PagedSkippedReviews>(`${BASE}/skipped-reviews`, {
      params: buildReviewQueryParams(opts, { page, size }),
      timeout: DEFAULT_TIMEOUT_MS,
    })
    .then(r => r.data)

/**
 * Wraps a CSV blob into a synthesised <a download> click so the browser saves
 * it locally without navigating. Shared by every CSV-export helper in this
 * service so the download mechanics stay in one place.
 */
const triggerCsvDownload = (data: BlobPart, filename: string): void => {
  const url = URL.createObjectURL(new Blob([data], { type: 'text/csv' }))
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

/**
 * Triggers a CSV download of all per-reviewer metrics.
 * Uses an extended timeout for large datasets.
 */
export const exportUserMetricsCSV = (): Promise<void> =>
  axios.get(`${BASE}/users/export`, { responseType: 'blob', timeout: EXPORT_TIMEOUT_MS }).then(r => {
    triggerCsvDownload(r.data, 'grapes-metrics-export.csv')
  })

/**
 * Triggers a CSV download of all per-leader metrics. Same shape as
 * {@link exportUserMetricsCSV}: no filters or sort — the leaders endpoint
 * returns the full list and the in-table search is client-side only.
 */
export const exportLeaderMetricsCSV = (): Promise<void> =>
  axios.get(`${BASE}/leaders/export`, { responseType: 'blob', timeout: EXPORT_TIMEOUT_MS }).then(r => {
    triggerCsvDownload(r.data, 'grapes-leaders-export.csv')
  })

/**
 * Triggers a CSV download of completed reviews matching the same filters as
 * {@link getCompletedReviews}. The backend honours every filter (period,
 * reviewer, search, sort) so the export reflects exactly what the admin sees
 * on screen, across all pages.
 */
export const exportCompletedReviewsCSV = (opts?: ReviewQueryOpts<CompletedReviewsSort>): Promise<void> =>
  axios
    .get(`${BASE}/completed-reviews/export`, {
      params: buildReviewQueryParams(opts),
      responseType: 'blob',
      timeout: EXPORT_TIMEOUT_MS,
    })
    .then(r => {
      triggerCsvDownload(r.data, 'grapes-completed-reviews-export.csv')
    })

/**
 * Triggers a CSV download of skipped reviews matching the same filters as
 * {@link getSkippedReviews}. Mirrors {@link exportCompletedReviewsCSV} so the
 * backend and frontend filter contracts stay aligned.
 */
export const exportSkippedReviewsCSV = (opts?: ReviewQueryOpts<SkippedReviewsSort>): Promise<void> =>
  axios
    .get(`${BASE}/skipped-reviews/export`, {
      params: buildReviewQueryParams(opts),
      responseType: 'blob',
      timeout: EXPORT_TIMEOUT_MS,
    })
    .then(r => {
      triggerCsvDownload(r.data, 'grapes-skipped-reviews-export.csv')
    })
